import Application from "../../Application";
import FindCourtCaseDetailsInteractor from "../../caseLawDocuments/interactor/FindCourtCaseDetailsInteractor";
import { ICourtCase } from "../../caseLawDocuments/model/CourtCase";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import GetOrganisationCaseDecisionTypesInteractor from "../../organisations/interactors/GetOrganisationCaseDecisionTypesInteractor";
import { IOrganisation } from "../../organisations/model/Organisation";
import CreateCaseDecisionInteractor from "../interactor/CreateCaseDecisionInteractor";
import GetAllCaseDecisionsInteractor from "../interactor/GetAllCaseDecisionsInteractor";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import GetCaseDecisionTypeIndexDataInteractor from "../interactor/GetCaseDecisionTypeIndexDataInteractor";
import { ICaseDecision } from "../model/CaseDecision";
import ShowWarningMessageInteractor from "../../core/interactor/notifications/ShowWarningMessageInteractor";
import { IUser } from "../../user/model/User";
import CaseDecisionPresentationModel, {
  TCaseDecisionPresentationModel
} from "./model/CaseDecisionPresentationModel";
import DocumentTypeIndexCategoryPresentationModel, {
  TDocumentTypeIndexCategoryPresentationModel
} from "../../documentTypes/presenter/DocumentTypeIndexCategoryPresentationModel";
import { IDocumentType } from "../../documentTypes/model/DocumentType";
import ShowInfoMessageInteractor from "../../core/interactor/notifications/ShowInfoMessageInteractor";

export interface TCaseDecisionPresenter extends TLoadingAwarePresenter {
  caseDecisionList: TCaseDecisionPresentationModel[];
  courtCase?: ICourtCase;

  caseDecisionTypes: IDocumentType[];
  caseDecisionTypeOrganisationIndex: TDocumentTypeIndexCategoryPresentationModel[];
  selectedCaseDecisionTypeId?: number;
  selectedOrganisationIndexIds: number[];
  selectedOrganisationId?: number;
  organisations: IOrganisation[];
  user?: IUser;
  createModalVisible: boolean;
}

export interface ICaseDecisionPresenter
  extends TCaseDecisionPresenter,
    TPresentable {
  loadReferenceValues(
    caseDecisionTypesList: IDocumentType[],
    contentTypes: IGenericCodebookItem[],
    organisations: IOrganisation[]
  ): void;
  getAllCaseDecisions(): void;
  createCaseDecision(caseDecision: ICaseDecision): void;
  findCourtCaseDetails(courtCaseNumber: string): Promise<boolean>;
  getAllCaseDecisionTypeIndexes(caseDecisionTypeId: number): void;
  setCaseDecisionTypeId(caseDecisionTypeId: number): void;
  setOrganisationId(organisationIndexId: number): void;
  loadCaseDecisions(caseDecisionList: ICaseDecision[]): void;
  showCaseDecisionDetails(id: string): void;
  onChangeCreateModalVisible(value: boolean): void;
  showCaseDecisionCreation(): void;
}

const defaultState: TCaseDecisionPresenter = {
  caseDecisionList: [],
  courtCase: undefined,
  caseDecisionTypes: [],
  caseDecisionTypeOrganisationIndex: [],
  selectedOrganisationIndexIds: [],
  organisations: [],
  createModalVisible: false
};

const CaseDecisionPresenter = withStore<
  ICaseDecisionPresenter,
  TCaseDecisionPresenter
>(({ application, store, loader, translate }): ICaseDecisionPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TCaseDecisionPresenter>();

  loader.start("caseDecisionLoader");
  const loadCaseDecisions = (caseDecisionList: ICaseDecision[]) => {
    const { organisations, caseDecisionTypes } =
      _store.getState<TCaseDecisionPresenter>();
    let caseDecisions = (caseDecisionList || []).map((item) => {
      const caseDecisionType = caseDecisionTypes?.find(
        (value) => value.id === item.documentTypeId
      );
      const organisation = organisations?.find(
        (value) => value.id === item.organisationId
      );
      return CaseDecisionPresentationModel(
        item,
        caseDecisionType,
        organisation
      );
    });
    const user = _store.getState<any>().user;
    _store.update({
      caseDecisionList: caseDecisions,
      user
    });
    loader.stop("caseDecisionLoader");
  };
  const loadReferenceValues = (
    caseDecisionTypes: IDocumentType[],
    contentTypes: IGenericCodebookItem[],
    organisations: IOrganisation[]
  ) => {
    const user = _store.getState<any>().user;
    if (user && user.isOperator()) {
      setOrganisationId(user.organisationId);
    }
    _store.update({ caseDecisionTypes, contentTypes, organisations });
    loader.stop("caseDecisionLoader");
  };

  const createCaseDecision = async (caseDecision: any) => {
    const { courtCase, selectedOrganisationId } =
      _store.getState<TCaseDecisionPresenter>();
    let elementList =
      caseDecision?.organisationIndexList &&
      caseDecision?.organisationIndexList.length > 0
        ? caseDecision.organisationIndexList.flat(1)
        : [];
    caseDecision.courtCaseId = courtCase?.id;
    caseDecision.courtCaseNumber = courtCase?.courtCaseNumber;
    caseDecision.organisationId = selectedOrganisationId;
    await _application.container
      .resolve<CreateCaseDecisionInteractor>("createCaseDecision")
      .execute(
        caseDecision,
        elementList.filter((item) => item !== null && item !== undefined)
      );
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project_member.create_success_message"));
    getAllCaseDecisions();
  };

  const getAllCaseDecisions = async () => {
    let caseDecisionList = await _application.container
      .resolve<GetAllCaseDecisionsInteractor>("getAllCaseDecisions")
      .execute();
    loadCaseDecisions(caseDecisionList);
    loader.stop("caseDecisionLoader");
  };

  const findCourtCaseDetails = async (courtCaseNumber: string) => {
    if (!courtCaseNumber || courtCaseNumber.length === 0) {
      _application.container
        .resolve<ShowWarningMessageInteractor>("showWarningMessage")
        .execute(
          translate("editors.case_law_documents.connected_cases.no_case_number")
        );
      return false;
    }
    var record = await _application.container
      .resolve<FindCourtCaseDetailsInteractor>("findCourtCaseDetails")
      .execute(courtCaseNumber);
    if (record.courtCaseNumber) {
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.case_law_documents.primary_cases.success"));
      _store.update({ courtCase: record });
    } else {
      _application.container
        .resolve<ShowInfoMessageInteractor>("showInfoMessage")
        .execute(
          translate("editors.case_law_documents.connected_cases.not_found")
        );
      _store.update({ courtCase: undefined });
    }
    return record !== undefined ?? false;
  };
  const setCaseDecisionTypeId = async (caseDecisionTypeId: number) => {
    _store.update({ selectedCaseDecisionTypeId: caseDecisionTypeId });
    await getAllCaseDecisionTypeIndexes(caseDecisionTypeId);
  };
  const setOrganisationId = async (organisationId: number) => {
    _store.update({ selectedOrganisationId: organisationId });
    await getAllOrganisationCaseDecisionTypes(organisationId);
  };

  const getAllCaseDecisionTypeIndexes = async (caseDecisionTypeId: number) => {
    const { selectedLanguage, selectedOrganisationId } = _store.getState<any>();
    let organisationIndexes = await _application.container
      .resolve<GetCaseDecisionTypeIndexDataInteractor>(
        "getCaseDecisionTypeIndexData"
      )
      .execute(caseDecisionTypeId, selectedOrganisationId);
    _store.update({
      caseDecisionTypeOrganisationIndex: organisationIndexes.map((item) =>
        DocumentTypeIndexCategoryPresentationModel(item, selectedLanguage)
      )
    });
  };
  const getAllOrganisationCaseDecisionTypes = async (
    selectedOrganisationId: number
  ) => {
    let caseDecisionTypes = await _application.container
      .resolve<GetOrganisationCaseDecisionTypesInteractor>(
        "getOrganisationCaseDecisionTypes"
      )
      .execute(selectedOrganisationId);
    if (caseDecisionTypes.length === 1) {
      _store.update({
        caseDecisionTypes,
        selectedCaseDecisionTypeId: caseDecisionTypes[0].id
      });
      getAllCaseDecisionTypeIndexes(caseDecisionTypes[0].id);
    } else {
      _store.update({
        caseDecisionTypes
      });
    }
  };

  const showCaseDecisionDetails = (id: string) => {
    return _application.navigator.push({
      pathname: `/case-decisions/${id}/details`
    });
  };
  const showCaseDecisionCreation = () => {
    return _application.navigator.push({
      pathname: `/case-decisions/new`
    });
  };
  const onChangeCreateModalVisible = (value: boolean) => {
    _store.update({ createModalVisible: value });
  };
  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadReferenceValues,
    loadCaseDecisions,
    getAllCaseDecisions,
    createCaseDecision,
    showCaseDecisionCreation,
    findCourtCaseDetails,
    getAllCaseDecisionTypeIndexes,
    setCaseDecisionTypeId,
    setOrganisationId,
    showCaseDecisionDetails,
    onChangeCreateModalVisible
  };
}, defaultState);

export default CaseDecisionPresenter;
